import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPinOff, ArrowRight } from 'lucide-react';
import AppLogo from '@/components/AppLogo';

// Real 404 page. Injects <meta name="robots" content="noindex"> so search
// engines drop unknown URLs instead of treating them as duplicate soft-404s.
export default function NotFoundPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Page Not Found (404) | How Busy Is My Airport';
    // Update the EXISTING robots meta in place (a second tag can be ignored
    // by some crawlers); restore the original directives on unmount.
    let meta = document.head.querySelector('meta[name="robots"]');
    const created = !meta;
    const prevContent = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, follow');
    meta.setAttribute('data-notfound', '1');
    return () => {
      if (created) {
        meta.remove();
      } else {
        meta.setAttribute('content', prevContent);
        meta.removeAttribute('data-notfound');
      }
      document.title = prevTitle;
    };
  }, []);

  const links = [
    { to: '/', label: 'Crowd predictor', desc: 'Check how busy your airport will be' },
    { to: '/airports', label: 'Airport guides', desc: 'Terminals, parking, and best times for major US hubs' },
    { to: '/live', label: 'Live dashboard', desc: 'Busiest US airports right now' },
    { to: '/blog', label: 'Travel blog', desc: 'Data-backed airport and travel guides' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="not-found-page">
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity w-fit">
          <AppLogo size={36} className="flex-shrink-0" />
          <span className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900">
            How Busy Is My Airport
          </span>
        </Link>
      </header>
      <main className="container mx-auto px-4 sm:px-6 py-16">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
            <MapPinOff className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-2">404 — Not Found</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            This gate doesn't exist
          </h1>
          <p className="text-slate-600 mb-10">
            The page you're looking for was moved, renamed, or never existed.
            Here's where most travelers want to go:
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-left">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`notfound-link-${l.to.replace(/\//g, '') || 'home'}`}
                className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-900">{l.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm text-slate-500">{l.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
